import type { ActiveRoom } from '@patrianna/shared-patrianna-types/store/BingoModule'
import type { GameProductShort } from '@patrianna/shared-patrianna-types/store/GameModule'
import { intlnumberFormat } from '@patrianna/shared-utils/numbers'
import cx from 'classnames'
import { usePathname, useRouter } from 'next/navigation'
import type { MouseEvent } from 'react'

import { Picture } from 'components/Picture'
import { ImageType } from 'components/Picture/Picture.types'
import images from 'config/images.json'
import { PARLAY_ROOM_CODES } from 'config/parlay-rooms-codes'
import FavoriteButton from 'containers/FavoriteButton'
import favoriteButtonClasses from 'containers/FavoriteButton/styles.module.scss'
import { useHorizontalPageIndex } from 'hooks/useHorizontalPageIndex'
import { useVerticalPageIndex } from 'hooks/useVerticalPageIndex'
import type { PictureProps } from 'src/components/Picture/Picture.types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getBingoRoomById, getBingoRoomJackpotPrize } from 'store/modules/bingo/selectors'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { startExternalGame } from 'store/modules/slotGameFlow/actions'
import { isLoggedInSelector } from 'store/modules/user/selectors'
import { extractBingoRoomId } from 'utils/jackpot'

import CardCost from './components/CardCost'
import CountdownBar from './components/CountdownBar'
import CountdownTimer from './components/CountdownTimer'
import GamePrize from './components/GamePrize'
import MoreInfoBtn from './components/MoreInfoBtn'
import TileTitle from './components/TileTitle'
import { useBingoTileBadges } from './hooks/useBingoTileBadges'
import classes from './styles.module.scss'

export type Props = {
  room: GameProductShort
  fullWidth?: boolean
  gameIndex?: number
  with3DImage?: boolean
  layout?: string
  hideFavorite?: boolean
}

function BingoTile(props: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const dispatch = useAppDispatch()
  const activeCurrency = useAppSelector(getActiveCurrencyIdSelector)
  const bingoRoom = useAppSelector((state) => getBingoRoomById(state, props.room.code))
  const extractedRoomId = extractBingoRoomId(props.room.code)
  const wsRoomId = String((props.room as unknown as ActiveRoom).roomId ?? extractedRoomId)
  const bingoRoomJackpot = useAppSelector((state) => getBingoRoomJackpotPrize(state, wsRoomId))
  const loggedIn = useAppSelector(isLoggedInSelector)
  const getVerticalPageIndex = useVerticalPageIndex()
  const getHorizontalPageIndex = useHorizontalPageIndex('bingo', false, pathname)
  const infoUrl = `/bingo/rooms/${props.room.route}`
  const xPageIndex = getHorizontalPageIndex(props?.gameIndex)
  const { jackpotTicker } = useBingoTileBadges({ bingoRoomJackpot })

  const badgeTopClass = props.with3DImage ? classes.badgeSlotTop3D : classes.badgeSlotTop

  const badgeSlot = jackpotTicker && (
    <div className={cx('absolute right-0 flex flex-col items-end drop-shadow-badge', badgeTopClass)}>
      {jackpotTicker}
    </div>
  )

  const favoriteButton = !props.hideFavorite && (
    <FavoriteButton
      className={cx(
        favoriteButtonClasses.bingoTileFavorite,
        props.with3DImage && favoriteButtonClasses.bingoTileFavorite3D,
      )}
      gameProduct={props.room}
      refererPath='game_tile'
    />
  )

  // use props.room.title for dev environment
  const roomTitle = process.env.PROFILE === 'dev' ? props.room.title : bingoRoom ? bingoRoom.roomName : props.room.title

  const src = props.with3DImage
    ? `${images.bucket}/ysi-images/bingo-tiles/3d-tiles/${props.room.title.replace(/ /g, '_')?.toLowerCase()}.png`
    : `${images.bucket}/bingo-tiles/${roomTitle.replace(/ /g, '_')?.toLowerCase()}/source.jpg`

  const pictureProps: PictureProps = {
    src,
    alt: roomTitle,
    className: cx(classes.img, props.with3DImage && classes.img3D),
    type: props.with3DImage ? ImageType['3D_TILE'] : ImageType.BINGO_TILE,
    loading: 'lazy',
    isWebpFirst: !props.with3DImage,
  }

  return (
    <div
      data-test='bingo-tile'
      data-testid='bingo-tile'
      className={cx('mt-paper', classes.tile, {
        '!bg-transparent !shadow-none': props.with3DImage,
      })}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        const bingoGameOpeningParams = {
          gameIndex: props?.gameIndex,
          refererPath: pathname,
          xPageIndex,
          yPageIndex: getVerticalPageIndex(event.clientY),
          layout: props.layout,
        }

        if (!props?.room?.code) {
          dispatch(startExternalGame({ type: 'bingo', code: 'bingo' }, router.push, bingoGameOpeningParams))
        } else {
          dispatch(startExternalGame(props.room, router.push, bingoGameOpeningParams))
        }
      }}
    >
      {bingoRoom ? (
        <>
          <div className={cx(classes.tileTop, props.with3DImage && classes.tileTop3D)}>
            {!props.with3DImage && process.env.PROFILE === 'dev' && <TileTitle title={bingoRoom.roomName} />}
            {!loggedIn && <MoreInfoBtn title={bingoRoom.roomName} infoUrl={infoUrl} />}
            <CountdownTimer nextGameStart={bingoRoom.nextGameStart} with3DImage={props.with3DImage} />
            {badgeSlot}
            {favoriteButton}
            <Picture {...pictureProps} />
          </div>
          <div className={classes.tileBottom}>
            <CountdownBar nextGameStart={bingoRoom.nextGameStart} />
            {bingoRoom?.bingoType && <div className={classes.balls}>{bingoRoom?.bingoType}</div>}
            <div className={classes.infoRow}>
              {bingoRoom?.gamePrize && (
                <GamePrize
                  gamePrizeAmount={intlnumberFormat(bingoRoom?.gamePrize[0]?.amount)}
                  activeCurrency={activeCurrency}
                />
              )}
              {bingoRoom?.cardCost && (
                <CardCost
                  cardCostValue={intlnumberFormat(Number(bingoRoom?.cardCost))}
                  activeCurrency={activeCurrency}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={cx(classes.tileTop, props.with3DImage && classes.tileTop3D)}>
            {/*TODO: temp solution to show MoreInfo button without critical changes remove after getBingoRoomsRequest update*/}
            {!loggedIn && PARLAY_ROOM_CODES.includes(props.room.code) && (
              <MoreInfoBtn title={props.room.title} infoUrl={infoUrl} />
            )}
            {!props.with3DImage && process.env.PROFILE === 'dev' && <TileTitle title={props.room?.title} />}
            {badgeSlot}
            {favoriteButton}
            <Picture {...pictureProps} />
          </div>
          {!props.with3DImage && <div className={classes.tileBottom} />}
        </>
      )}
    </div>
  )
}

export default BingoTile
